import { CONFIG } from './config';
import { FuelSession, fuelStarCount } from './fuel';
import { fuelZones, generateFuelLayout } from './fuel-layout';
import type { Narration } from './narration';
import type { VoiceId } from './voices';

export const FUEL_CONFIG = {
  idleHintMs: 8000,
  flyMs: 450,
  enterMs: 900,
  duplicateWobbleMs: 400,
  resizeDebounceMs: 150,
  /** Minimum visual pacing so a muted run (or very short audio) never rushes. */
  afterCountMinMs: 450,
  readyMinMs: 900,
  countdownStepMinMs: 650,
  launchMs: 1100,
  /** A stalled audio element can never freeze the launch for longer than this. */
  audioCapMs: 4000,
} as const;

/** Rocket art aspect ratio (width / height) of /art/fuel-rocket.svg. */
const ROCKET_ASPECT = 240 / 400;

export interface FuelActivityRefs {
  screen: HTMLElement;
  starsLayer: HTMLElement;
  numeral: HTMLElement;
  total: HTMLElement;
  rocket: HTMLElement;
  slots: HTMLElement;
  countdown: HTMLElement;
  caption: HTMLElement;
  hud: HTMLElement;
  muteButton: HTMLButtonElement;
  muteIcon: HTMLImageElement;
  hearButton: HTMLButtonElement;
  completeOverlay: HTMLElement;
  replayButton: HTMLButtonElement;
  backButton: HTMLButtonElement;
  level1MuteIcon?: HTMLImageElement;
}

export type FuelNarration = Pick<
  Narration,
  'play' | 'pickFuelVoice' | 'stop' | 'cancelQueued' | 'setMuted'
> & {
  readonly muted_: boolean;
};

export interface FuelActivityOptions {
  onBack?: () => void;
  /** Random source for targets and layouts (tests inject a deterministic one). */
  random?: () => number;
}

function seededRandom(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export class FuelActivity {
  private session: FuelSession;
  private voice: VoiceId = 'computer';
  private starsMap = new Map<number, HTMLButtonElement>();
  private timers = new Set<number>();
  private idleTimer: number | null = null;
  private resizeTimer: number | null = null;
  private layoutSeed = 1;
  private launchesStarted = 0;
  private launchesCompleted = 0;
  private lastCountPlay: Promise<void> = Promise.resolve();
  private windowListeners: Array<() => void> = [];
  private reducedMotion = false;
  private readonly random: () => number;

  constructor(
    private refs: FuelActivityRefs,
    private narration: FuelNarration,
    private options?: FuelActivityOptions,
  ) {
    this.random = options?.random ?? Math.random;
    this.session = new FuelSession(this.random);
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.attachHud();
    this.attachWindowListeners();
    this.syncMute(this.narration.muted_);
  }

  async begin(): Promise<void> {
    this.startRun();
  }

  async replay(): Promise<void> {
    this.startRun();
  }

  suspend(): void {
    this.session.invalidate();
    this.resetRuntime();
    this.refs.completeOverlay.hidden = true;
    this.refs.screen.hidden = true;
  }

  dispose(): void {
    this.suspend();
    for (const remove of this.windowListeners) {
      remove();
    }
    this.windowListeners = [];
  }

  /** Full restart: new session, first round, intro line. */
  private startRun(): void {
    this.resetRuntime();
    this.launchesStarted = 0;
    this.launchesCompleted = 0;
    this.refs.completeOverlay.hidden = true;
    this.refs.screen.dataset.launchesStarted = '0';
    this.refs.screen.dataset.launchesCompleted = '0';
    const epoch = this.session.start();
    this.setupRound(epoch, true);
  }

  /** Invalidates in-flight narration and clears every timer and transient class. */
  private resetRuntime(): void {
    for (const timer of this.timers) {
      clearTimeout(timer);
    }
    this.timers.clear();
    this.idleTimer = null;
    if (this.resizeTimer !== null) {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = null;
    }
    this.narration.cancelQueued();
    this.narration.stop();
    this.refs.countdown.dataset.visible = 'false';
    this.refs.countdown.textContent = '';
    this.refs.rocket.classList.remove('is-entering');
    for (const btn of this.starsMap.values()) {
      btn.classList.remove('is-wobble', 'is-nudge');
    }
  }

  private after(ms: number, fn: () => void): number {
    const id = window.setTimeout(() => {
      this.timers.delete(id);
      fn();
    }, ms);
    this.timers.add(id);
    return id;
  }

  /** Resolves after ms; never resolves if the timer is cleared by a reset. */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      this.after(ms, resolve);
    });
  }

  /** Plays a line but stops waiting for it after audioCapMs (visual flow never hangs on audio). */
  private say(id: string): Promise<void> {
    return Promise.race([
      this.narration.play(id, { voice: this.voice }),
      this.delay(FUEL_CONFIG.audioCapMs),
    ]).catch(() => undefined);
  }

  private setupRound(epoch: number, withIntro: boolean): void {
    const { screen, total, rocket } = this.refs;
    const target = this.session.target;
    if (!withIntro) {
      // Drop any lines of the finished launch that are still queued behind a slow clip.
      this.narration.cancelQueued();
    }
    this.voice = this.narration.pickFuelVoice();
    this.layoutSeed = Math.floor(this.random() * 0x7fffffff) + 1;

    screen.dataset.voice = this.voice;
    screen.dataset.target = String(target);
    screen.dataset.count = '0';
    screen.dataset.round = String(this.session.launchIndex + 1);
    screen.dataset.phase = this.session.phase;
    this.refs.numeral.textContent = String(target);
    total.textContent = `0 of ${target} fuel collected`;
    this.refs.countdown.dataset.visible = 'false';
    this.refs.countdown.textContent = '';

    this.renderSlots(target);
    this.renderStars(fuelStarCount(target));
    this.applyLayout();

    rocket.dataset.state = 'idle';
    rocket.classList.remove('is-entering');
    if (!this.reducedMotion) {
      void rocket.offsetWidth;
      rocket.classList.add('is-entering');
      this.after(FUEL_CONFIG.enterMs, () => {
        if (this.session.isCurrent(epoch)) rocket.classList.remove('is-entering');
      });
    }

    if (withIntro) {
      void this.narration.play('fuel-intro', { voice: this.voice });
    }
    this.speakNeeds();
    this.startIdleHintTimer();
  }

  private speakNeeds(): void {
    void this.narration.play('fuel-needs', { voice: this.voice });
    void this.narration.play('count-' + this.session.target, { voice: this.voice });
  }

  private attachHud(): void {
    this.refs.muteButton.addEventListener('click', () => {
      const next = !this.narration.muted_;
      this.narration.setMuted(next);
      try {
        localStorage.setItem(CONFIG.muteStorageKey, next ? '1' : '0');
      } catch {
        /* localStorage may be unavailable in private browsing mode */
      }
      this.syncMute(next);
    });

    this.refs.hearButton.addEventListener('click', () => {
      if (this.session.phase !== 'collecting') return;
      this.startIdleHintTimer();
      this.nudgeUncollectedStar();
      this.narration.cancelQueued();
      this.speakNeeds();
    });

    this.refs.replayButton.addEventListener('click', () => {
      void this.replay();
    });

    this.refs.backButton.addEventListener('click', () => {
      this.suspend();
      this.options?.onBack?.();
    });
  }

  private syncMute(muted: boolean): void {
    const src = muted ? '/art/icon-speaker-off.svg' : '/art/icon-speaker-on.svg';
    this.refs.muteIcon.src = src;
    const l1 =
      this.refs.level1MuteIcon ?? (document.getElementById('mute-icon') as HTMLImageElement | null);
    if (l1) {
      l1.src = src;
    }
    const l2 = document.getElementById('count-mute-icon') as HTMLImageElement | null;
    if (l2) {
      l2.src = src;
    }
  }

  private renderSlots(target: number): void {
    const { slots } = this.refs;
    slots.innerHTML = '';
    // Few, large sockets per row: 1 row up to 3, 2 rows up to 6, 3 rows beyond.
    const cols = target <= 3 ? target : target === 4 ? 2 : target <= 9 ? 3 : 4;
    slots.style.setProperty('--slot-cols', String(cols));
    slots.dataset.rows = String(Math.ceil(target / cols));
    for (let i = 0; i < target; i++) {
      const slot = document.createElement('span');
      slot.className = 'fuel-slot';
      slot.dataset.filled = 'false';
      slots.appendChild(slot);
    }
  }

  private renderStars(count: number): void {
    const layer = this.refs.starsLayer;
    layer.innerHTML = '';
    this.starsMap.clear();
    for (let id = 0; id < count; id++) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'fuel-star';
      btn.dataset.starId = String(id);
      btn.dataset.collected = 'false';
      btn.setAttribute('aria-label', 'Fuel star');
      btn.style.touchAction = 'manipulation';

      const img = document.createElement('img');
      img.src = '/art/fuel-star.svg';
      img.alt = '';
      img.draggable = false;
      btn.appendChild(img);

      btn.addEventListener('click', () => {
        this.onStarClick(id, btn);
      });

      layer.appendChild(btn);
      this.starsMap.set(id, btn);
    }
  }

  /** Positions rocket, numeral badge, countdown and stars from the current viewport. */
  private applyLayout(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const zones = fuelZones(w, h);
    const rz = zones.rocket;

    const phone = w <= 500 || h <= 499;
    const portrait = zones.orientation === 'portrait';
    // Numeral badge sits at the zone's top-left; the rocket uses the room to its right.
    const badgeMax = phone && !portrait ? 84 : 150;
    const badge = Math.round(Math.max(72, Math.min(badgeMax, Math.min(rz.width, rz.height) * 0.4)));
    const gap = 10;
    const roomW = Math.max(1, rz.width - badge - gap);
    let rocketH = Math.min(rz.height * (phone ? 0.98 : 0.92), roomW / ROCKET_ASPECT);
    if (phone && portrait) {
      // Taller than the zone: the star field is re-fitted below the rocket.
      rocketH = Math.min(h * 0.39, 330, roomW / ROCKET_ASPECT);
    } else if (phone) {
      // Phone landscape: the rocket may reach down towards the caption (about 72px).
      rocketH = Math.min(h - 72 - 12, roomW / ROCKET_ASPECT);
    }
    rocketH = Math.max(1, rocketH);
    const rocketW = rocketH * ROCKET_ASPECT;
    const rocketLeft = rz.x + badge + gap + (roomW - rocketW) / 2;
    const rocketTop = phone
      ? portrait
        ? rz.y
        : Math.min(rz.y, 12)
      : rz.y + (rz.height - rocketH) * 0.3;
    const rocket = this.refs.rocket;
    rocket.style.left = `${rocketLeft}px`;
    rocket.style.top = `${rocketTop}px`;
    rocket.style.width = `${rocketW}px`;
    rocket.style.height = `${rocketH}px`;

    const numeral = this.refs.numeral;
    numeral.style.left = `${rz.x + 4}px`;
    numeral.style.top = `${rz.y + 6}px`;
    numeral.style.width = `${badge}px`;
    numeral.style.height = `${badge}px`;
    numeral.style.fontSize = `${Math.round(badge * 0.8)}px`;

    const sz = zones.stars;
    const cd = this.refs.countdown;
    const cdSize = Math.round(Math.max(120, Math.min(260, Math.min(w, h) * 0.28)));
    cd.style.left = `${rocketLeft + rocketW / 2 - cdSize / 2}px`;
    cd.style.top = `${rocketTop + rocketH * 0.24 - cdSize / 2}px`;
    cd.style.width = `${cdSize}px`;
    cd.style.height = `${cdSize}px`;
    cd.style.fontSize = `${Math.round(cdSize * 0.85)}px`;

    // Star field: normally the zone as given; when the rocket grew past its zone
    // (phone portrait) the same layout is fitted into the shorter region below it.
    const starsBottom = sz.y + sz.height;
    const starsTop = Math.max(sz.y, rocketTop + rocketH + 8);
    let layoutH = h;
    let dy = 0;
    if (portrait && starsTop > sz.y + 0.5) {
      const wantH = Math.max(1, starsBottom - starsTop);
      let lo = w + 1;
      let hi = h;
      for (let i = 0; i < 24; i++) {
        const mid = (lo + hi) / 2;
        if (fuelZones(w, mid).stars.height > wantH) hi = mid;
        else lo = mid;
      }
      layoutH = lo;
      dy = starsTop - fuelZones(w, layoutH).stars.y;
    }
    const layout = generateFuelLayout(
      w,
      layoutH,
      this.starsMap.size,
      seededRandom(this.layoutSeed),
    );
    for (const slot of layout) {
      const btn = this.starsMap.get(slot.id);
      if (!btn) continue;
      btn.style.left = `${slot.cx - slot.size / 2}px`;
      btn.style.top = `${slot.cy + dy - slot.size / 2}px`;
      btn.style.width = `${slot.size}px`;
      btn.style.height = `${slot.size}px`;
    }

    const first = this.refs.slots.firstElementChild as HTMLElement | null;
    const socket = first ? first.getBoundingClientRect().width : 0;
    this.refs.slots.classList.toggle('is-small-sockets', socket > 0 && socket < 28);
  }

  private onStarClick(id: number, btn: HTMLButtonElement): void {
    const result = this.session.collect(id);
    if (result.kind === 'ignored') {
      if (result.reason === 'duplicate') this.wobble(btn);
      return;
    }

    const epoch = result.epoch;
    const { screen, total } = this.refs;
    this.clearNudge();

    btn.dataset.collected = 'true';
    btn.setAttribute('aria-disabled', 'true');
    btn.tabIndex = -1;
    const slot = this.refs.slots.children[result.count - 1] as HTMLElement | undefined;
    if (slot) {
      slot.dataset.filled = 'true';
    }
    screen.dataset.count = String(result.count);
    total.textContent = `${result.count} of ${this.session.target} fuel collected`;

    this.lastCountPlay = this.narration.play('count-' + result.count, { voice: this.voice });
    this.flyToSlot(btn, slot);

    if (result.complete) {
      this.beginLaunch(epoch);
    } else {
      this.startIdleHintTimer();
    }
  }

  private flyToSlot(btn: HTMLButtonElement, slot: HTMLElement | undefined): void {
    if (!slot) return;
    if (!this.reducedMotion) {
      const from = btn.getBoundingClientRect();
      const to = slot.getBoundingClientRect();
      btn.style.setProperty(
        '--fly-x',
        `${to.left + to.width / 2 - (from.left + from.width / 2)}px`,
      );
      btn.style.setProperty(
        '--fly-y',
        `${to.top + to.height / 2 - (from.top + from.height / 2)}px`,
      );
      btn.style.setProperty(
        '--fly-scale',
        String(Math.max(0.1, to.width / Math.max(1, from.width))),
      );
    }
    btn.classList.add('is-flying');
  }

  private wobble(btn: HTMLButtonElement): void {
    if (this.reducedMotion) return;
    btn.classList.remove('is-wobble');
    void btn.offsetWidth;
    btn.classList.add('is-wobble');
    this.after(FUEL_CONFIG.duplicateWobbleMs, () => {
      btn.classList.remove('is-wobble');
    });
  }

  private beginLaunch(epoch: number): void {
    const { screen } = this.refs;
    this.clearIdleHintTimer();
    this.launchesStarted += 1;
    screen.dataset.phase = this.session.phase;
    screen.dataset.launchesStarted = String(this.launchesStarted);
    for (const [id, btn] of this.starsMap) {
      if (!this.session.isCollected(id)) {
        btn.dataset.inert = 'true';
        btn.setAttribute('aria-disabled', 'true');
        btn.tabIndex = -1;
      }
    }
    void this.runLaunch(epoch);
  }

  private async runLaunch(epoch: number): Promise<void> {
    const alive = () => this.session.isCurrent(epoch);
    const { rocket, countdown } = this.refs;

    await Promise.all([
      Promise.race([this.lastCountPlay, this.delay(FUEL_CONFIG.audioCapMs)]).catch(() => undefined),
      this.delay(FUEL_CONFIG.afterCountMinMs),
    ]);
    if (!alive()) return;

    rocket.dataset.state = 'ready';
    await Promise.all([this.say('fuel-ready'), this.delay(FUEL_CONFIG.readyMinMs)]);
    if (!alive()) return;

    rocket.dataset.state = 'shaking';
    for (const n of [3, 2, 1]) {
      countdown.textContent = String(n);
      countdown.dataset.visible = 'true';
      await Promise.all([this.say('count-' + n), this.delay(FUEL_CONFIG.countdownStepMinMs)]);
      if (!alive()) return;
    }
    countdown.dataset.visible = 'false';
    countdown.textContent = '';

    rocket.dataset.state = 'launching';
    await Promise.all([this.say('blast-off'), this.delay(FUEL_CONFIG.launchMs)]);
    if (!alive()) return;

    rocket.dataset.state = 'away';
    this.finishLaunch(epoch);
  }

  private finishLaunch(epoch: number): void {
    const outcome = this.session.nextRound(epoch);
    if (outcome === 'stale') return;
    this.launchesCompleted += 1;
    this.refs.screen.dataset.launchesCompleted = String(this.launchesCompleted);

    if (outcome === 'next') {
      this.setupRound(this.session.epoch, false);
      return;
    }

    this.refs.screen.dataset.phase = this.session.phase;
    this.refs.numeral.textContent = '';
    this.refs.starsLayer.innerHTML = '';
    this.starsMap.clear();
    void this.narration.play('fuel-great', { voice: this.voice });
    this.refs.completeOverlay.hidden = false;
    this.refs.replayButton.focus({ preventScroll: true });
  }

  private startIdleHintTimer(): void {
    this.clearIdleHintTimer();
    const epoch = this.session.epoch;
    this.idleTimer = this.after(FUEL_CONFIG.idleHintMs, () => {
      this.idleTimer = null;
      if (!this.session.isCurrent(epoch) || this.session.phase !== 'collecting') return;
      this.nudgeUncollectedStar();
      this.speakNeeds();
    });
  }

  private clearIdleHintTimer(): void {
    if (this.idleTimer !== null) {
      clearTimeout(this.idleTimer);
      this.timers.delete(this.idleTimer);
      this.idleTimer = null;
    }
    this.clearNudge();
  }

  private nudgeUncollectedStar(): void {
    this.clearNudge();
    for (const [id, btn] of this.starsMap) {
      if (!this.session.isCollected(id)) {
        btn.classList.add('is-nudge');
        break;
      }
    }
  }

  private clearNudge(): void {
    for (const btn of this.starsMap.values()) {
      btn.classList.remove('is-nudge');
    }
  }

  private attachWindowListeners(): void {
    const onResizeOrRotate = () => {
      if (this.resizeTimer !== null) {
        clearTimeout(this.resizeTimer);
      }
      this.resizeTimer = window.setTimeout(() => {
        this.resizeTimer = null;
        if (this.refs.screen.hidden || this.starsMap.size === 0) return;
        this.applyLayout();
      }, FUEL_CONFIG.resizeDebounceMs);
    };

    window.addEventListener('resize', onResizeOrRotate);
    window.addEventListener('orientationchange', onResizeOrRotate);

    this.windowListeners.push(() => {
      window.removeEventListener('resize', onResizeOrRotate);
      window.removeEventListener('orientationchange', onResizeOrRotate);
    });
  }
}
